import moment from 'moment';
import React, { useEffect } from 'react';
import { ICampaign, IDevice, IUser } from '../../interfaces';
import { flattenCampaigns, getWeekNumber, groupCampaigns } from '../../utils';
import { ENDPOINT, IS_TVOS, TOKEN } from '../../utils/constants';
import { panelMergeSameName } from '../../utils/panel';
import { getShopFromDevices } from '../../utils/pricing';
import {
    alignWeeks,
    generateDevicesHashMap,
    generateWeeksData,
    getSlotsAvailable,
    getUsedSpace
} from '../../utils/weeks-viewer';

const MONTHS_EQ = {
    January: 'Janvier',
    February: 'Février',
    March: 'Mars',
    April: 'Avril',
    May: 'Mai',
    June: 'Juin',
    July: 'Juillet',
    August: 'Aout',
    September: 'Septembre',
    October: 'Octobre',
    November: 'Novembre',
    December: 'Décembre'
};

function numberWithSpaces(x: number) {
    return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
}

export const getDevicesFromCamp = (devices: number[], deviceMap: { [key: number]: IDevice }) => {
    const outDevices: IDevice[] = [];

    for (const d of devices) {
        if (d in deviceMap) {
            outDevices.push(deviceMap[d]);
        }
    }

    return outDevices;
};

export const PanelView = (props: {
    campaigns: ICampaign[];
    devices: IDevice[];
    date: Date;
    users: IUser[];
    variables: { year: number; current: number; goal: number, agentId: number }[];
    lastUpdate: Date;
    onRequestUpdate: () => void;
    onChangeYear: (plus: boolean) => void;
    onSelectWeek: (weekNumber: number) => void;
}) => {
    const onChangeYear = props.onChangeYear;
    const onRequestUpdate = props.onRequestUpdate;
    const groupedCampaigns = flattenCampaigns(groupCampaigns(props.campaigns), true);
    const { weeks } = generateWeeksData(groupedCampaigns, props.date);
    const mergedWeeks = panelMergeSameName(weeks);
    const devices = generateDevicesHashMap(props.devices);

    alignWeeks(mergedWeeks);

    useEffect(() => {
        const onKeyPress = (e: KeyboardEvent) => {
            if (e.code === 'ArrowLeft' || e.keyCode === 4) {
                onChangeYear(false);
            } else if (e.code === 'ArrowRight' || e.keyCode === 5) {
                onChangeYear(true);
            } else if (e.code === 'KeyR') {
                onRequestUpdate();
            }
        };

        window.addEventListener('keydown', onKeyPress);

        const refreshTo = setInterval(() => onRequestUpdate(), 300000);

        return () => {
            window.removeEventListener('keydown', onKeyPress);
            clearInterval(refreshTo);
        };
    }, [onChangeYear, onRequestUpdate]);

    useEffect(() => {
        const scrollToWeek = () => {
            const weekNumber = getWeekNumber(new Date());
            if (weekNumber <= 2) return;

            const wnElt = document.getElementById(`w-${weekNumber - 2}`);
            if (wnElt) wnElt.scrollIntoView();
        };

        const scrollTo = setInterval(() => scrollToWeek(), 14400000);
        scrollToWeek();

        return () => {
            clearInterval(scrollTo);
        };
    }, []);

    let aid = 1;

    if (window.location.host === 'planning-sport.doohyoulike.com') aid = 8;
    else if (window.location.host === 'planning-campus.doohyoulike.com') aid = 10;

    const devicesNumArray = props.devices.map(({ id }) => id);
    const now = new Date();
    let month = mergedWeeks[0].dateStart.getMonth();
    let objective = props.variables.find(({ year, agentId }) => year === now.getFullYear() && agentId === aid) as {
        year: number;
        current: number;
        goal: number;
    };

    if (!objective) {
        objective = {
            year: now.getFullYear(),
            goal: 1,
            current: 0
        };
    }

    return (
        <div className="panelView">
            <div className="topbar">
                <div>Planning {props.date.getFullYear()}</div>
                <div>Dernière mise à jour: {moment(props.lastUpdate).format('HH:mm')}</div>
            </div>
            <div className="columns">
                <div className="col col1">
                    <div className="tble">
                        {mergedWeeks.map((w, i) => {
                            const campaigns = (w.campaigns as ICampaign[]).filter((c) => c !== null);

                            const usedSpaceRatio = getUsedSpace(
                                campaigns,
                                devicesNumArray.length,
                                new Date(w.dateStart)
                            );

                            const slotsAvailable = getSlotsAvailable(
                                (weeks[i].campaigns as ICampaign[]).filter((c) => c !== null),
                                devicesNumArray,
                                new Date(w.dateStart)
                            );
                            const endOfWeek = new Date(w.dateStart);
                            endOfWeek.setDate(endOfWeek.getDate() + 7);
                            let numColor = '#ddd';
                            let texColor = '#000';
                            let borderColor = '#eee';
                            let isNewMonth = false;
                            let passed = false;

                            if (w.dateStart.getTime() < now.getTime() && endOfWeek.getTime() > now.getTime()) {
                                numColor = '#f9302c';
                                texColor = '#fff';
                            } else if (w.dateStart.getTime() < now.getTime()) {
                                numColor = '#eee';
                                texColor = '#aaa';
                                passed = true;
                            }

                            if (w.dateStart.getMonth() !== month) {
                                month = w.dateStart.getMonth();
                                borderColor = '#999';
                                isNewMonth = true;
                            }

                            return (
                                <div
                                    key={i}
                                    className="line"
                                    style={{ borderTopColor: borderColor, opacity: passed ? 0.6 : 1 }}
                                >
                                    {(!isNewMonth && i !== 0) || (
                                        <div className="newMonth">
                                            {MONTHS_EQ[moment(w.dateStart).format('MMMM') as keyof typeof MONTHS_EQ]}
                                        </div>
                                    )}
                                    <div
                                        id={`w-${i}`}
                                        onClick={() => {
                                            // props.onSelectWeek(i + 1);
                                        }}
                                        className="num"
                                    >
                                        <div className="wnum" style={{ backgroundColor: numColor, color: texColor }}>
                                            {i + 1}
                                        </div>
                                        <div>Esp. libres: {slotsAvailable}</div>
                                        <div className="day">{moment(w.dateStart).format('DD/MM')}</div>
                                        <div className="progress" style={{ width: `${100 * usedSpaceRatio}%` }}></div>
                                    </div>
                                    {w.campaigns.map((c, j) => (
                                        <div className="camp" key={`${c?.id}-${j}`}>
                                            {!c || (
                                                <div className="logo">
                                                    <img
                                                        src={c?.logoUrl || 'https://via.placeholder.com/40x40'}
                                                        alt="logo"
                                                    />
                                                </div>
                                            )}
                                            {!c || (
                                                <div className="details">
                                                    <div className="campName">{c.name}</div>
                                                    <div className="screens">
                                                        <span className="material-icons" style={{ marginRight: 4 }}>
                                                            tv
                                                        </span>
                                                        {c.devices.length} |
                                                        <span
                                                            className="material-icons"
                                                            style={{ marginRight: 4, marginLeft: 3 }}
                                                        >
                                                            storefront
                                                        </span>
                                                        {
                                                            Object.keys(
                                                                getShopFromDevices(
                                                                    getDevicesFromCamp(c.devices, devices)
                                                                )
                                                            ).length
                                                        }{' '}
                                                        {!c.hideFromInterface || (
                                                            <React.Fragment>
                                                                |
                                                                <span
                                                                    className="material-icons"
                                                                    style={{ margin: '0px 2px' }}
                                                                >
                                                                    remove_shopping_cart
                                                                </span>
                                                            </React.Fragment>
                                                        )}
                                                        {!!c.active || c.state !== 'passed' || (
                                                            <React.Fragment>
                                                                |
                                                                <span
                                                                    className="material-icons"
                                                                    style={{ margin: '0px 2px', color: '#f9302c' }}
                                                                >
                                                                    pause
                                                                </span>
                                                            </React.Fragment>
                                                        )}
                                                        {!!c.active || (
                                                            <React.Fragment>
                                                                |
                                                                <span
                                                                    className="draft-indicator"
                                                                    style={{
                                                                        marginLeft: 3,
                                                                        backgroundColor:
                                                                            c.state === 'passed' ? '#63cb89' : '#f9302c'
                                                                    }}
                                                                ></span>
                                                            </React.Fragment>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
            <div className="botright">
                <div className="nav">
                    <div onClick={() => onChangeYear(false)}>
                        <span className="material-icons">arrow_back_ios</span>
                    </div>
                    <div>
                        <span onClick={() => onChangeYear(true)} className="material-icons">
                            arrow_forward_ios
                        </span>
                    </div>
                </div>
                <div className="objective">
                    <p
                        className="obj"
                        style={{ cursor: 'pointer' }}
                        onClick={() => {
                            if (IS_TVOS) {
                                return;
                            }

                            const new_obj = window.prompt('Objectif', objective.goal.toString());

                            if (new_obj) {
                                const goal = parseFloat(new_obj);

                                if (isNaN(goal)) {
                                    return;
                                }

                                let aid = 1;

                                if (window.location.host === 'planning-sport.doohyoulike.com') aid = 8;
                                else if (window.location.host === 'planning-campus.doohyoulike.com') aid = 10;
                                fetch(`${ENDPOINT}/revenue/${aid}`, {
                                    method: 'PATCH',
                                    headers: {
                                        'x-planning-token': TOKEN,
                                        'Content-Type': 'application/json'
                                    },
                                    body: JSON.stringify({
                                        year: now.getFullYear(),
                                        goal,
                                        current: objective.current
                                    })
                                }).then(() => props.onRequestUpdate());
                            }
                        }}
                    >
                        Objectif: {numberWithSpaces(objective.goal)} &euro;
                    </p>
                    <div className="current-ctr">
                        <span
                            className="current"
                            style={{ cursor: 'pointer' }}
                            onClick={() => {
                                if (IS_TVOS) {
                                    return;
                                }

                                const new_obj = window.prompt('Revenu actuel', objective.current.toString());

                                if (new_obj) {
                                    const current = parseFloat(new_obj);

                                    if (isNaN(current)) {
                                        return;
                                    }

                                    let aid = 1;
                                    if (window.location.host === 'planning-sport.doohyoulike.com') aid = 8;
                                    else if (window.location.host === 'planning-campus.doohyoulike.com') aid = 10;
                                    fetch(`${ENDPOINT}/revenue/${aid}`, {
                                        method: 'PATCH',
                                        headers: {
                                            'x-planning-token': TOKEN,
                                            'Content-Type': 'application/json'
                                        },
                                        body: JSON.stringify({
                                            year: now.getFullYear(),
                                            current,
                                            goal: objective.goal
                                        })
                                    }).then(() => props.onRequestUpdate());
                                }
                            }}
                        >
                            {numberWithSpaces(objective.current)} &euro;
                        </span>
                        <div className="sep"></div>
                        <span>{Math.round((10000 * objective.current) / objective.goal) / 100}%</span>
                        <div
                            className="bar-bottom"
                            style={{
                                width: `${Math.min(
                                    Math.round((10000 * objective.current) / objective.goal) / 100,
                                    100
                                )}%`
                            }}
                        ></div>
                    </div>
                </div>
            </div>
        </div>
    );
};
